import { useEffect } from "react";
import { useRouterState } from "@tanstack/react-router";

/**
 * GA4 + click tracking.
 * - Uses the existing sitewide gtag from __root.tsx. Does NOT load a second tag.
 * - Sends page_view on every client-side route change.
 * - Delegates tel: and mailto: clicks to a single document listener so events
 *   fire exactly once per click regardless of re-renders.
 */

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    __drAnalyticsClicks?: boolean;
    __drLastPageViewPath?: string;
  }
}

export function Analytics() {
  const pathname = useRouterState({ select: (s) => s.location.pathname + (s.location.searchStr ?? "") });

  // SPA page_view tracking.
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    if (window.__drLastPageViewPath === pathname) return;
    window.__drLastPageViewPath = pathname;
    window.gtag("event", "page_view", {
      page_location: window.location.href,
      page_path: pathname,
      page_title: document.title,
    });
  }, [pathname]);

  // Global tel/mailto click delegation, registered once.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.__drAnalyticsClicks) return;
    window.__drAnalyticsClicks = true;

    const handler = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (!target || typeof target.closest !== "function") return;
      const a = target.closest('a[href]') as HTMLAnchorElement | null;
      if (!a) return;
      const href = a.getAttribute("href") || "";
      if (typeof window.gtag !== "function") return;
      if (/^tel:/i.test(href)) {
        window.gtag("event", "click_to_call", {
          page_location: window.location.href,
          link_location: a.closest("footer") ? "footer" : a.closest("header") ? "header" : "body",
        });
      } else if (/^mailto:/i.test(href)) {
        window.gtag("event", "click_to_email", {
          page_location: window.location.href,
          link_location: a.closest("footer") ? "footer" : a.closest("header") ? "header" : "body",
        });
      }
    };
    document.addEventListener("click", handler, { capture: true });
    return () => document.removeEventListener("click", handler, { capture: true } as EventListenerOptions);
  }, []);

  return null;
}
